import React, {useContext, useMemo, useState} from "react";
import "./ContentDeclarationTVA.scss";
import {EventUpdatedContext} from "../../../../store/EventUpdatedContext";
import {getIdWithToken, getToken} from "../../../../utils/storage/Token";
import {TypeForm} from "../../../../types/EnumTypeForm";
import FCContentModalFL, {DataForm} from "../../../FCObjets/Form/FCContentModalFL";
import {DeleteExpense, useFetchExpense, DownloadExpenseArchive} from "../../../../service/Business/ExpenseService";
import FCSpinner from "../../../FCObjets/FCSpinner";
import {ExpenseItemInterface} from "../../../../service/Business/Interfaces/ExpenseInterface";
import FCTag from "../../../FCObjets/FCTag";
import TypeDate from "../../../../types/TypeDate";
import {AppSettings} from "../../../../AppSettings";
import FCSortableHeader from "../../../FCObjets/FCSortableHeader";
import {useTableSort} from "../../../../hook/useTableSort";
import formatEuro from "../../../../utils/formatEuro";
import {useToast} from "../../../FCObjets/FCToast/FCToast";

type TvaSortKey = "description" | "date" | "priceExclTax" | "vat" | "priceVAT" | "priceInclTax" | "receiptLoaded";
function ContentDeclarationTVA() {
    const toast = useToast();

    const {cptUpdated, setUpdated} = useContext(EventUpdatedContext);

    const [period, setPeriod] = useState<{month: number; year: number}>({
        month: new Date().getMonth(),
        year: new Date().getFullYear()
    });
    const [, setIsModalOpened] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [archiveLoading, setArchiveLoading] = useState(false);

    const {data: expense, loading, error} = useFetchExpense(getIdWithToken(), period.month, period.year, cptUpdated);

    const [data, setData] = useState<DataForm>({
        type: TypeForm.EMPTY,
        data: undefined,
    });

    const gestionModal = (open: boolean, e: any, type: TypeForm | undefined) => {
        setIsModalOpened(open);
        setData({
            type: (open && type) ? type : TypeForm.EMPTY,
            data: e
        });
    };

    const gestionPeriod = (addMonth: boolean) => {
        let {month, year} = period;
        if (addMonth) {
            if (month === 11) { month = 0; year++; }
            else { month++; }
        } else {
            if (month === 0) { month = 11; year--; }
            else { month--; }
        }
        setPeriod({month, year});
    };

    const filteredExpenses = useMemo(() => {
        if (!expense?.listExpense) return [];
        const q = searchQuery.toLowerCase();
        return expense.listExpense.filter((e: ExpenseItemInterface) =>
            e.description.toLowerCase().includes(q)
        );
    }, [expense, searchQuery]);

    const {sortedData: sortedExpenses, sortState, handleSort} = useTableSort<ExpenseItemInterface, TvaSortKey>(
        filteredExpenses,
        {
            description: (e) => e.description,
            date: (e) => new Date(e.date),
            priceExclTax: (e) => e.priceExclTax,
            vat: (e) => e.vat,
            priceVAT: (e) => e.priceVAT,
            priceInclTax: (e) => e.priceInclTax,
            receiptLoaded: (e) => e.receiptLoaded,
        },
    );

    const handleDelete = (e: ExpenseItemInterface) => {
        const payload = {
            title: `Suppression de la dépense "${e.description}"`,
            msg: "Êtes-vous sûr de vouloir supprimer cette dépense ?",
            onConfirm: () => DeleteExpense(e.id).then(() => {
                toast.success("Dépense supprimée.", "Dépense");
                setUpdated();
            })
        };
        gestionModal(true, payload, TypeForm.CONFIRM);
    };

    const handleViewReceipt = (e: ExpenseItemInterface) => {
        const date = new Date(e.date);
        const path = "stockage_volume/" + e.userCnx?.username + "/TVA/" + date.getFullYear() + "/" + TypeDate.monthsLong[date.getMonth()] + "/" + e.nameFile;
        const rawToken = getToken()?.replace("Bearer ", "") || "";
        const url = `${AppSettings.API_ENDPOINT}${AppSettings.API_FREELANCERIE}/explorer/view/${getIdWithToken().toString()}/${encodeURIComponent(path)}?token=${rawToken}`;
        window.open(url, '_blank');
    };

    const handleDownloadArchive = () => {
        setArchiveLoading(true);
        DownloadExpenseArchive(getIdWithToken(), period.month, period.year)
            .catch(() => { /* erreur silencieuse */ })
            .finally(() => setArchiveLoading(false));
    };

    const renderActions = (e: ExpenseItemInterface, isMobile: boolean) => {
        const btnClass = isMobile ? "tvaMobileActionBtn" : "tvaActionBtn";
        return (
            <>
                <button className={`${btnClass} tvaActionEdit`}
                        onClick={() => gestionModal(true, e, TypeForm.EXPENSE)}
                        title="Modifier">
                    <span className="material-symbols-outlined">edit</span>
                    {isMobile && "Modifier"}
                </button>
                {e.receiptLoaded === 0 && (
                    <button className={`${btnClass} tvaActionUpload`}
                            onClick={() => gestionModal(true, e, TypeForm.UPLOAD_EXPENSE)}
                            title="Archiver">
                        <span className="material-symbols-outlined">upload_file</span>
                        {isMobile && "Archiver"}
                    </button>
                )}
                {e.receiptLoaded === 1 && (
                    <button className={`${btnClass} tvaActionView`}
                            onClick={() => handleViewReceipt(e)}
                            title="Visualiser">
                        <span className="material-symbols-outlined">visibility</span>
                        {isMobile && "Voir"}
                    </button>
                )}
                <button className={`${btnClass} tvaActionDelete`}
                        onClick={() => handleDelete(e)}
                        title="Supprimer">
                    <span className="material-symbols-outlined">delete</span>
                    {isMobile && "Supprimer"}
                </button>
            </>
        );
    };

    return (
        <FCContentModalFL form={data} onModalFormFinished={() => {
            gestionModal(false, undefined, undefined);
            setUpdated();
        }}>
            <div className="tvaPage">
                {/* Header */}
                <div className="tvaHeader">
                    <div>
                        <h1 className="tvaTitle">Déclaration de TVA</h1>
                        <p className="tvaSubtitle">Période fiscale du 1er au {new Date(period.year, period.month + 1, 0).getDate()} {TypeDate.monthsLong[period.month]} {period.year}</p>
                    </div>
                    <div className="tvaHeaderRight">
                        <div className="tvaSearchBar">
                            <span className="material-symbols-outlined tvaSearchIcon">search</span>
                            <input className="tvaSearchInput"
                                    type="text"
                                    placeholder="Rechercher une dépense..."
                                    value={searchQuery}
                                    onChange={(ev) => setSearchQuery(ev.target.value)}/>
                        </div>
                        <button className="tvaBtnAction tvaBtnArchive"
                                onClick={handleDownloadArchive}
                                disabled={archiveLoading || !expense || filteredExpenses.length === 0}
                                title="Télécharger l'archive du mois (PDF récapitulatif + justificatifs)">
                            <span className="material-symbols-outlined">{archiveLoading ? "hourglass_empty" : "folder_zip"}</span>
                            Archive
                        </button>
                        <button className="tvaBtnAction"
                                onClick={() => gestionModal(true, undefined, TypeForm.EXPENSE)}>
                            <span className="material-symbols-outlined">add</span>
                            Nouvelle dépense
                        </button>
                    </div>
                </div>

                {/* Bouton mobile */}
                <button className="tvaMobileFab"
                        onClick={() => gestionModal(true, undefined, TypeForm.EXPENSE)}>
                    <span className="material-symbols-outlined">add</span>
                    Nouvelle dépense
                </button>

                {/* Period nav */}
                <div className="tvaPeriodNav">
                    <button className="tvaPeriodBtn" onClick={() => gestionPeriod(false)}>
                        <span className="material-symbols-outlined">chevron_left</span>
                    </button>
                    <span className="tvaPeriodLabel">{TypeDate.monthsLong[period.month]} {period.year}</span>
                    <button className="tvaPeriodBtn" onClick={() => gestionPeriod(true)}>
                        <span className="material-symbols-outlined">chevron_right</span>
                    </button>
                </div>

                {loading && <div className="tvaLoading"><FCSpinner size="middle"/></div>}
                {error && <div className="tvaEmptyState">{error.message}</div>}

                {expense && (
                    <>
                        {/* KPI Cards */}
                        <div className="tvaKpiGrid">
                            <div className="tvaKpiCard tvaKpiNette">
                                <div className="tvaKpiLabel">TVA nette due</div>
                                <div className="tvaKpiValueLg">{formatEuro(expense.vatDeclared)}</div>
                                <div className="tvaKpiSub">À reverser à l'État</div>
                            </div>
                            <div className="tvaKpiCard">
                                <div className="tvaKpiLabel">TVA brute (collectée)</div>
                                <div className="tvaKpiValue">{formatEuro(expense.vatMonth)}</div>
                                <div className="tvaKpiSub">Sur les factures émises</div>
                            </div>
                            <div className="tvaKpiCard">
                                <div className="tvaKpiLabel">TVA déductible</div>
                                <div className="tvaKpiValue">{formatEuro(expense.vatSum)}</div>
                                <div className="tvaKpiSub">Sur {filteredExpenses.length} dépense{filteredExpenses.length > 1 ? "s" : ""}</div>
                            </div>
                        </div>

                        {/* Section header */}
                        <div className="tvaSectionHeader">
                            <h2 className="tvaSectionTitle">Détails des dépenses déductibles</h2>
                            
                        </div>

                        {/* Desktop Table */}
                        <div className="tvaTableWrapper">
                            <table className="tvaTable">
                                <thead>
                                <tr>
                                    <th><FCSortableHeader label="Description" sortKey="description" sortState={sortState} onSort={handleSort}/></th>
                                    <th><FCSortableHeader label="Date" sortKey="date" sortState={sortState} onSort={handleSort}/></th>
                                    <th><FCSortableHeader label="Prix HT" sortKey="priceExclTax" sortState={sortState} onSort={handleSort}/></th>
                                    <th><FCSortableHeader label="Taux" sortKey="vat" sortState={sortState} onSort={handleSort}/></th>
                                    <th><FCSortableHeader label="Prix TVA" sortKey="priceVAT" sortState={sortState} onSort={handleSort}/></th>
                                    <th><FCSortableHeader label="Prix TTC" sortKey="priceInclTax" sortState={sortState} onSort={handleSort}/></th>
                                    <th><FCSortableHeader label="Justificatif" sortKey="receiptLoaded" sortState={sortState} onSort={handleSort}/></th>
                                    <th>Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {sortedExpenses.map((e: ExpenseItemInterface, index: React.Key) => (
                                    <tr key={index}>
                                        <td>
                                            <span className="tvaCellDescName">{e.description}</span>
                                        </td>
                                        <td>{new Date(e.date).toLocaleDateString()}</td>
                                        <td><span className="tvaCellAmount">{formatEuro(e.priceExclTax)}</span></td>
                                        <td>{(e.vat * 100).toFixed(0)}%</td>
                                        <td><span className="tvaCellAmountGold">{formatEuro(e.priceVAT)}</span></td>
                                        <td>{formatEuro(e.priceInclTax)}</td>
                                        <td>
                                            <FCTag
                                                value={e.receiptLoaded === 1 ? "Valide" : e.receiptLoaded === 2 ? "Compte" : "En attente"}
                                                color={e.receiptLoaded === 1 ? "green" : e.receiptLoaded === 2 ? "blue" : "orange"}
                                                style={{fontWeight: "bold"}}/>
                                        </td>
                                        <td>
                                            <div className="tvaCellActions">
                                                {renderActions(e, false)}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {sortedExpenses.length === 0 && (
                                    <tr>
                                        <td colSpan={8}>
                                            <div className="tvaEmptyState">Aucune dépense pour cette période</div>
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                            <div className="tvaTableFooter">
                                <span className="tvaTableCount">
                                    {sortedExpenses.length} dépense{sortedExpenses.length > 1 ? "s" : ""}
                                </span>
                            </div>
                        </div>

                        {/* Mobile Cards */}
                        <div className="tvaMobileList">
                            {sortedExpenses.map((e: ExpenseItemInterface, index: React.Key) => (
                                <div className="tvaMobileCard" key={index}>
                                    <div className="tvaMobileCardHeader">
                                        <div>
                                            <h3 className="tvaMobileCardName">{e.description}</h3>
                                            <FCTag
                                                value={e.receiptLoaded === 1 ? "Valide" : e.receiptLoaded === 2 ? "Compte" : "En attente"}
                                                color={e.receiptLoaded === 1 ? "green" : e.receiptLoaded === 2 ? "blue" : "orange"}
                                                style={{fontWeight: "bold"}}/>
                                        </div>
                                    </div>
                                    <div className="tvaMobileCardMeta">
                                        {new Date(e.date).toLocaleDateString()}
                                    </div>
                                    <div className="tvaMobileCardAmounts">
                                        <div className="tvaMobileCardAmountCol">
                                            <span className="tvaMobileCardAmountLabel">Prix HT</span>
                                            <span className="tvaMobileCardAmountValue">{formatEuro(e.priceExclTax)}</span>
                                        </div>
                                        <div className="tvaMobileCardAmountCol">
                                            <span className="tvaMobileCardAmountLabel">TVA</span>
                                            <span className="tvaMobileCardAmountValueGold">{formatEuro(e.priceVAT)}</span>
                                        </div>
                                        <div className="tvaMobileCardAmountCol">
                                            <span className="tvaMobileCardAmountLabel">TTC</span>
                                            <span className="tvaMobileCardAmountValue">{formatEuro(e.priceInclTax)}</span>
                                        </div>
                                        <div className="tvaMobileCardAmountCol">
                                            <span className="tvaMobileCardAmountLabel">Taux</span>
                                            <span className="tvaMobileCardAmountValue">{(e.vat * 100).toFixed(0)}%</span>
                                        </div>
                                    </div>
                                    <div className="tvaMobileCardActions">
                                        {renderActions(e, true)}
                                    </div>
                                </div>
                            ))}
                            {sortedExpenses.length === 0 && (
                                <div className="tvaEmptyState">Aucune dépense pour cette période</div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </FCContentModalFL>
    );
}

export default ContentDeclarationTVA;
